import cloneDeep from 'lodash/cloneDeep';
import { applySnapshot, cast, flow, Instance, types } from 'mobx-state-tree';
import moment from 'moment';
import { formatDateToISO, getReportPeriod, killNulls } from '../../../utils';
import {
  compareNoExecutorLast,
  NO_EXECUTOR_DEPARTMENT_ID,
  NO_EXECUTOR_LABEL,
  transformCategories,
  transformWorkers,
} from '../../../utils/requests-utils';
import { REPORT_STATUSES, ReportStatus } from '../../../constants';
import { Reports } from '../../../http-requests';
import { RequestStateModel } from '../general-models';
import { RequestStatusMST } from '../requestsStore';

const RequestsModel = types.model({
  deptData: types.optional(RequestStateModel, {}),
  providerData: types.optional(RequestStateModel, {}),
  categoriesData: types.optional(RequestStateModel, {}),
  dayInfo: types.optional(RequestStateModel, {}),
  history: types.optional(RequestStateModel, {}),
  historyById: types.optional(RequestStateModel, {}),
  report: types.optional(RequestStateModel, {}),
  reportLink: types.optional(RequestStateModel, {}),
  createExcelReport: types.optional(RequestStateModel, {}),
});

const ProviderStatModel = types.model({
  total: types.optional(types.number, 0),
  accepted: types.optional(types.number, 0),
  run: types.optional(types.number, 0),
  performed: types.optional(types.number, 0),
  delayed: types.optional(types.number, 0),
  refusal: types.optional(types.number, 0),
  abandonment: types.optional(types.number, 0),
});

// Поля-массивы и поля-флаги параметров отчёта.
const REPORT_ARRAY_PARAM_KEYS = [
  'category',
  'subcategory',
  'work_area',
  'actions__action__in',
  'house__id__in',
  'departments__in',
  'positions__in',
  'executors__in',
  'total_rate__in',
];

const REPORT_BOOLEAN_PARAM_KEYS = [
  'administrative_supervision',
  'housing_supervision',
];

// dispatcher сохраняет params отчёта (история) через dict(req.query_params):
// значения схлопываются в строки (category: "emergency"), тогда как модель
// ожидает массивы/boolean. Поэтому приведение снапшота к ожидаемой форме до валидации MST,
// иначе конвертация падает и история показывает "Нет данных".
const preProcessReportParams = (snapshot: any): any => {
  if (!snapshot || typeof snapshot !== 'object') {
    return snapshot;
  }

  const result: Record<string, any> = { ...snapshot };

  REPORT_ARRAY_PARAM_KEYS.forEach((key) => {
    const value = result[key];

    if (value === undefined || value === null || value === '') {
      delete result[key];
    } else if (!Array.isArray(value)) {
      result[key] = [value];
    }
  });

  REPORT_BOOLEAN_PARAM_KEYS.forEach((key) => {
    if (typeof result[key] === 'string') {
      result[key] = result[key] === 'true';
    }
  });

  return result;
};

const ParamsBaseModel = types
  .model({
    dt_from: types.optional(types.maybeNull(types.string), null),
    dt_till: types.optional(types.maybeNull(types.string), null),
    type: types.optional(types.maybeNull(types.string), null),
    category: types.optional(types.array(types.string), []),
    subcategory: types.optional(types.array(types.string), []),
    work_area: types.optional(types.array(types.string), []),
    actions__action__in: types.optional(types.array(types.string), []),
    house__id__in: types.optional(types.array(types.string), []),
    departments__in: types.optional(types.array(types.string), []),
    positions__in: types.optional(types.array(types.string), []),
    executors__in: types.optional(types.array(types.string), []),
    total_rate__in: types.optional(types.array(types.string), []),
    administrative_supervision: types.optional(
      types.maybeNull(types.boolean),
      null
    ),
    housing_supervision: types.optional(types.maybeNull(types.boolean), null),
  })
  .actions((self) => ({
    setParams(newParams: IRequestsReportParamsModel) {
      applySnapshot(self, newParams);
    },
    setStringProperties(key: 'dt_from' | 'dt_till', value: string | null) {
      self[key] = value;
    },
    resetParams() {
      self.dt_from = formatDateToISO(moment().startOf('month'));
      self.dt_till = formatDateToISO(moment());
      self.type = null;
      self.category = cast([]);
      self.subcategory = cast([]);
      self.work_area = cast([]);
      self.actions__action__in = cast([]);
      self.house__id__in = cast([]);
      self.departments__in = cast([]);
      self.positions__in = cast([]);
      self.executors__in = cast([]);
      self.total_rate__in = cast([]);
      self.administrative_supervision = null;
      self.housing_supervision = null;
    },
  }));

// Обёртка с нормализацией снапшота: строки из истории dispatcher приводятся
// к массивам/булевым перед валидацией, при этом типы полей остаются прежними
// (читатели работают с массивами как и раньше).
const ParamsModel = types.snapshotProcessor(ParamsBaseModel, {
  preProcessor: preProcessReportParams,
});

export interface IRequestsReportParamsModel extends Instance<
  typeof ParamsModel
> {}

const HistoryViewedModel = types.model({
  name: types.optional(types.string, ''),
  position: types.optional(types.string, ''),
  provider_id: types.optional(types.string, ''),
  date: types.optional(types.maybeNull(types.string), null),
  inn: types.optional(types.string, ''),
});

const HistoryWorkerModel = types.model({
  id: types.optional(types.string, ''),
  name: types.optional(types.string, ''),
  position: types.optional(types.string, ''),
  provider_id: types.optional(types.string, ''),
});

const HistoryActionModel = types.model({
  name: types.optional(types.string, ''),
  is_linked: types.optional(types.boolean, false),
  is_saved: types.optional(types.boolean, false),
  linked_date: types.optional(types.maybeNull(types.string), null),
});

const HistoryListModel = types.model({
  id: types.optional(types.string, ''),
  created_at: types.optional(types.string, ''),
  viewed: types.optional(types.array(HistoryViewedModel), []),
  worker: types.optional(HistoryWorkerModel, {}),
  action: types.optional(HistoryActionModel, {}),
  params: types.optional(ParamsModel, {}),
});

export interface IRequestsReportHistoryListModel extends Instance<
  typeof HistoryListModel
> {}

const FiltersModel = types
  .model({
    dt_from: types.optional(types.string, ''),
    dt_till: types.optional(types.string, ''),
    workers: types.optional(types.array(types.string), []),
    viewed: types.optional(types.array(types.string), []),
  })
  .actions((self) => ({
    setDateFilters(startDate: string, endDate: string) {
      self.dt_from = startDate;
      self.dt_till = endDate;
    },
    setWorkersFilter(workers: string[]) {
      self.workers = cast(workers);
    },
    setViewedFilter(options: string[]) {
      self.viewed = cast(options);
    },
  }));

const HistoryModel = types
  .model({
    list: types.optional(types.array(HistoryListModel), []),
    filteredList: types.optional(types.array(HistoryListModel), []),
    filters: types.optional(FiltersModel, {}),
  })
  .views((self) => ({
    get allWorkersNames() {
      return Array.from(new Set(self.list.map((item) => item.worker.name)));
    },
  }))
  .actions((self) => ({
    resetFilters() {
      self.filters.workers = cast(self.allWorkersNames);
      self.filters.viewed = cast([]);
      self.filters.dt_from = formatDateToISO(moment().startOf('month'));
      self.filters.dt_till = formatDateToISO(moment());
    },
    filterList() {
      const cloneList = cloneDeep(self.list);

      let filteredList = cloneList.filter((item) => {
        return moment(item.created_at).isBetween(
          moment(self.filters.dt_from),
          moment(self.filters.dt_till).add(1, 'day'),
          null,
          '[]'
        );
      });

      filteredList = filteredList.filter((item) =>
        self.filters.workers.includes(item.worker.name)
      );

      if (self.filters.viewed.length > 0) {
        filteredList = filteredList.filter((item) => {
          const isViewedFilterApplied =
            self.filters.viewed.includes('Просмотрена');
          const isViewed = item.viewed.length > 0;
          const isLinkedAction = item.action.is_linked;
          const isViewedConditionMet = isViewedFilterApplied
            ? self.filters.viewed.includes('Не просмотрена') || isViewed
            : item.viewed.length === 0;

          return isLinkedAction && isViewedConditionMet;
        });
      }

      self.filteredList = cast(filteredList);
    },
  }));

export interface IRequestsReportHistoryModel extends Instance<
  typeof HistoryModel
> {}

const StatusModel = types.model({
  performed: types.optional(types.number, 0),
  delayed: types.optional(types.number, 0),
  accepted: types.optional(types.number, 0),
  refusal: types.optional(types.number, 0),
  abandonment: types.optional(types.number, 0),
  run: types.optional(types.number, 0),
  hidden: types.optional(types.number, 0),
});

const DeptStatusesModel = types.model({
  status: types.optional(RequestStatusMST, 'accepted'),
  count: types.optional(types.number, 0),
});

export interface IDeptStatusesModel extends Instance<
  typeof DeptStatusesModel
> {}

const DeptModel = types
  .model({
    _id: types.optional(types.string, ''),
    name: types.optional(types.maybeNull(types.string), ''),
    // Уникальные заявки отдела из request_by_department (не сумма по сотрудникам)
    requests: types.optional(types.number, 0),
    statuses: types.optional(types.array(DeptStatusesModel), []),
  })
  .views((self) => ({
    get totalInfoByDepartment() {
      return self.statuses.reduce(
        (acc, curr: IDeptStatusesModel) => {
          acc['total'] += curr.count;
          acc[curr.status] += curr.count || 0;

          return acc;
        },
        {
          total: 0,
          accepted: 0,
          run: 0,
          performed: 0,
          delayed: 0,
          refusal: 0,
          abandonment: 0,
          hidden: 0,
        }
      );
    },
  }));

const DeptWorkerModel = types.model({
  id: types.optional(types.maybeNull(types.string), ''),
  name: types.optional(types.maybeNull(types.string), ''),
  total: types.optional(types.number, 0),
  statuses: types.optional(StatusModel, {}),
  avg_rate: types.optional(types.maybeNull(types.string), null),
  department_name: types.optional(types.maybeNull(types.string), ''),
  position_name: types.optional(types.maybeNull(types.string), ''),
});

export interface IDeptWorkerModel extends Instance<typeof DeptWorkerModel> {}

const CategoryDateModel = types.model({
  date: types.optional(types.string, ''),
  count: types.optional(types.number, 0),
});

export interface IRequestsReportCategoryDateModel extends Instance<
  typeof CategoryDateModel
> {}

const CategoryModel: any = types.model({
  name: types.optional(types.string, ''),
  count: types.optional(types.number, 0),
  dates: types.optional(types.array(CategoryDateModel), []),
  subcategories: types.optional(
    types.array(types.late(() => CategoryModel)),
    []
  ),
});

export interface IRequestsReportCategoryModel extends Instance<
  typeof CategoryModel
> {}

const DaysDataModel = types.model({
  id: types.optional(types.string, ''),
  number: types.optional(types.string, ''),
  category: types.optional(types.string, ''),
  subcategory: types.optional(types.maybeNull(types.string), null),
});

export const RequestsReportStore = types
  .model({
    loading: types.optional(RequestsModel, {}),
    params: types.optional(ParamsModel, {}),
    saved_dt_from: types.optional(types.maybeNull(types.string), null),
    saved_dt_till: types.optional(types.maybeNull(types.string), null),
    selectedDateInTable: types.optional(types.string, ''),
    history: types.optional(HistoryModel, {}),
    history_report_requests_id: types.optional(types.string, ''),
    isReportFromOtherProvider: types.optional(types.boolean, false),
    deptData: types.optional(types.maybeNull(types.array(DeptModel)), null),
    deptWorkersData: types.optional(
      types.map(types.array(DeptWorkerModel)),
      {}
    ),
    providerStat: types.optional(types.maybeNull(ProviderStatModel), null),
    categoriesData: types.optional(
      types.maybeNull(types.map(types.array(CategoryModel))),
      null
    ),
    daysData: types.optional(
      types.map(types.map(types.array(DaysDataModel))),
      {}
    ),
    showHistoryModal: types.optional(types.boolean, false),
  })
  .views((self) => {
    const views = {
      get filtersCount(): number {
        const { params } = self;
        const hasCategoryFilter =
          params.category.length > 0 ||
          params.subcategory.length > 0 ||
          params.work_area.length > 0;

        const filters = [
          params.dt_from && params.dt_till,
          params.type !== null,
          hasCategoryFilter,
          params.actions__action__in.length > 0,
          params.house__id__in.length > 0,
          params.departments__in.length > 0,
          params.positions__in.length > 0,
          params.executors__in.length > 0,
          params.total_rate__in.length > 0,
          params.administrative_supervision,
          params.housing_supervision,
        ];

        return filters.reduce(
          (count, filter) => (filter ? count + 1 : count),
          0
        );
      },
      get isFormDisabled(): boolean {
        return (
          self.loading.deptData.pending ||
          self.loading.providerData.pending ||
          self.loading.categoriesData.pending ||
          !self.params.dt_from ||
          !self.params.dt_till
        );
      },
      get totalDeptsStat() {
        const result = {
          total: 0,
          accepted: 0,
          run: 0,
          performed: 0,
          delayed: 0,
          refusal: 0,
          abandonment: 0,
        };

        for (const workers of Array.from(self.deptWorkersData.values())) {
          workers?.forEach((worker: IDeptWorkerModel) => {
            result['total'] += worker.total;
            result['accepted'] += worker.statuses.accepted;
            result['run'] += worker.statuses.run;
            result['performed'] += worker.statuses.performed;
            result['delayed'] += worker.statuses.delayed;
            result['refusal'] += worker.statuses.refusal;
            result['abandonment'] += worker.statuses.abandonment;
          });
        }

        return result;
      },
      // Итог организации: уникальные заявки из request_by_provider
      get organizationTotalStat() {
        if (self.providerStat) {
          return {
            total: self.providerStat.total,
            accepted: self.providerStat.accepted,
            run: self.providerStat.run,
            performed: self.providerStat.performed,
            delayed: self.providerStat.delayed,
            refusal: self.providerStat.refusal,
            abandonment: self.providerStat.abandonment,
          };
        }

        return views.totalDeptsStat;
      },
    };

    return views;
  })
  .actions((self) => ({
    setBooleanProperties(key: 'showHistoryModal', value: boolean) {
      self[key] = value;
    },
    setStringProperties(key: 'selectedDateInTable', value: string) {
      self[key] = value;
    },
    clearAll() {
      self.history_report_requests_id = '';
      self.selectedDateInTable = self.params.dt_from || '';
      self.deptData = null;
      self.deptWorkersData = cast({});
      self.providerStat = null;
      self.categoriesData = null;
      self.daysData = cast({});
    },
    getReportProviderData: flow(function* (id?: string) {
      try {
        self.loading.providerData.loadingStates();

        const data = {
          ...killNulls({
            ...self.params,
            id: id || null,
          }),
          ...getReportPeriod(self.params.dt_from, self.params.dt_till),
          data: 'json',
        };

        const response = yield Reports.GetRequestsReportProviderData(data);
        const statuses = Object.fromEntries(
          (response.request_statuses || []).map(
            (item: { status: string; count: number }) => [
              item.status,
              item.count,
            ]
          )
        );

        self.providerStat = cast({
          total: response.provider_total_requests || 0,
          accepted: statuses.accepted || 0,
          run: statuses.run || 0,
          performed: statuses.performed || 0,
          delayed: statuses.delayed || 0,
          refusal: statuses.refusal || 0,
          abandonment: statuses.abandonment || 0,
        });

        self.loading.providerData.successStates();
      } catch (e) {
        self.loading.providerData.errorStates();
        self.providerStat = null;
        console.error(e);
      } finally {
        self.loading.providerData.finallyStates();
      }
    }),
    getReportDeptData: flow(function* (id?: string) {
      try {
        self.isReportFromOtherProvider = false;
        self.loading.deptData.fulfilled = false;
        self.loading.deptData.error = false;
        self.loading.deptData.pending = true;
        self.saved_dt_from = self.params.dt_from;
        self.saved_dt_till = self.params.dt_till;

        const data = {
          ...killNulls({
            ...self.params,
            id: id || null,
          }),
          ...getReportPeriod(self.params.dt_from, self.params.dt_till),
          data: 'json',
        };

        const response = yield Reports.GetRequestsReportDeptData(data);

        const rawDepts = response.department;
        self.deptData = cast(
          rawDepts.map((d: any) => ({
            _id: d.department_id ?? NO_EXECUTOR_DEPARTMENT_ID,
            name:
              d.department_id === null ? NO_EXECUTOR_LABEL : d.department_name,
            requests: d.department_total_requests,
            statuses: d.department_request_statuses,
          }))
        );
        self.deptWorkersData = cast(
          transformWorkers(rawDepts, self.params.executors__in)
        );
        self.history_report_requests_id = response.history_report_requests;

        self.loading.deptData.fulfilled = true;
      } catch (e: any) {
        self.loading.deptData.error = true;
        console.error(e);

        if (e.message) {
          self.isReportFromOtherProvider = true;
        }
      } finally {
        self.loading.deptData.pending = false;
      }
    }),
    getReportCategoriesData: flow(function* () {
      try {
        self.loading.categoriesData.fulfilled = false;
        self.loading.categoriesData.error = false;
        self.loading.categoriesData.pending = true;

        const data = {
          ...killNulls(self.params),
          ...getReportPeriod(self.params.dt_from, self.params.dt_till),
        };

        if (
          !self.categoriesData?.has(
            moment(self.selectedDateInTable).format('MM.yyyy')
          )
        ) {
          if (!self.categoriesData) {
            self.categoriesData = cast({});
          }

          const months = [];
          const current = moment(moment(self.params.dt_from).startOf('month'));

          while (
            current.isSameOrBefore(moment(self.params.dt_till).endOf('month'))
          ) {
            months.push(current.format('MM.YYYY'));
            current.add(1, 'month').startOf('month');
          }

          let response = yield Reports.GetRequestsReportCategoriesData(data);

          if (!moment().isSame(moment(self.params.dt_till), 'day')) {
            response = response.filter(
              (el: any) => !moment().isSame(moment(el.created_at), 'day')
            );
          }

          for (const month of months) {
            self.categoriesData?.set(
              month,
              cast(transformCategories(response, month))
            );
          }
        }

        self.loading.categoriesData.fulfilled = true;
      } catch (e) {
        self.loading.categoriesData.error = true;
        console.error(e);
      } finally {
        self.loading.categoriesData.pending = false;
      }
    }),
    getDayInfo: flow(function* (day: string, category: string) {
      try {
        self.loading.dayInfo.fulfilled = false;
        self.loading.dayInfo.error = false;
        self.loading.dayInfo.pending = true;

        if (
          self.daysData.has(category) &&
          self.daysData.get(category)?.has(day)
        ) {
          return;
        }

        const data = {
          ...self.params,
          day,
          category,
          ...getReportPeriod(day, day),
        };

        const response = yield Reports.GetRequestsReportDayInfo(
          killNulls(data)
        );

        response.forEach((item: any) => {
          const primaryKind = item.subcategory || item.category;
          const secondaryKind = item.category;

          const addToMap = (kind: string, item: any) => {
            if (!self.daysData.has(kind)) {
              self.daysData.set(kind, cast({}));
            }

            if (!self.daysData.get(kind)?.has(day)) {
              self.daysData.get(kind)?.set(day, cast([]));
            }

            self.daysData.get(kind)?.get(day)?.push(item);
          };

          addToMap(primaryKind, item);

          if (primaryKind !== secondaryKind) {
            addToMap(secondaryKind, item);
          }
        });

        if (!self.daysData.has(category)) {
          self.daysData.set(category, cast({}));
        }

        self.daysData.get(category)?.set(day, response);

        self.loading.dayInfo.fulfilled = true;
      } catch (e) {
        self.loading.dayInfo.error = true;
        console.error(e);
      } finally {
        self.loading.dayInfo.pending = false;
      }
    }),
    getHistoryData: flow(function* () {
      try {
        self.loading.history.fulfilled = false;
        self.loading.history.error = false;
        self.loading.history.pending = true;

        const response = yield Reports.GetRequestsReportHistory();

        self.history.list = cast(response);
        self.history.filteredList = cast(response);
        self.history.filters.workers = cast(self.history.allWorkersNames);

        self.loading.history.fulfilled = true;
      } catch (e) {
        self.loading.history.error = true;
        console.error(e);
      } finally {
        self.loading.history.pending = false;
      }
    }),
    getHistoryById: flow(function* (id: string) {
      try {
        self.loading.historyById.fulfilled = false;
        self.loading.historyById.error = false;
        self.loading.historyById.pending = true;

        const response = yield Reports.GetRequestsReportHistoryById({ id });
        self.params = cast(response.params);
        self.selectedDateInTable = formatDateToISO(
          moment(response.params.dt_from)
        );

        self.loading.historyById.fulfilled = true;
      } catch (e) {
        self.loading.historyById.error = true;
        console.error(e);
      } finally {
        self.loading.historyById.pending = false;
      }
    }),
    createExcelReport: flow(function* () {
      try {
        self.loading.createExcelReport.fulfilled = false;
        self.loading.createExcelReport.error = false;
        self.loading.createExcelReport.pending = true;

        const departments: Record<string, object[]> = {};
        const department_totals: Record<
          string,
          {
            department_total_requests: number;
            department_request_statuses: { status: string; count: number }[];
          }
        > = {};

        // «Без исполнителя» отправляется последним: порядок ключей определяет
        // порядок отделов в файле
        const deptEntries = Array.from(self.deptWorkersData.entries()).sort(
          ([a], [b]) => compareNoExecutorLast(a, b)
        );

        for (const [, workers] of deptEntries) {
          if (!workers || workers.length === 0) {
            continue;
          }

          const deptName = workers[0].department_name ?? '';
          departments[deptName] = workers.map((w) => ({
            employee_fullname: w.name,
            employee_position_name: w.position_name,
            total_requests: w.total,
            avg_rate: w.avg_rate,
            request_statuses: REPORT_STATUSES.map((status) => ({
              status,
              count: w.statuses[status as ReportStatus],
            })),
          }));
        }

        for (const dept of Array.from(self.deptData || []).sort((a, b) =>
          compareNoExecutorLast(a._id, b._id)
        )) {
          department_totals[dept.name ?? ''] = {
            department_total_requests: dept.requests,
            department_request_statuses: dept.statuses.map((item) => ({
              status: item.status,
              count: item.count,
            })),
          };
        }

        const totalStat = self.organizationTotalStat;
        const data = {
          dt_from: moment(self.params.dt_from).format('YYYY-MM-DD'),
          dt_till: moment(self.params.dt_till).format('YYYY-MM-DD'),
          departments,
          department_totals,
          total: {
            provider_total_requests: totalStat.total,
            request_statuses: REPORT_STATUSES.map((status) => ({
              status,
              count: totalStat[status as ReportStatus],
            })),
          },
        };

        const response = yield Reports.CreateExcelRequestsReport(data);
        window.open('/req/stat/request_by_department/xlsx/?id=' + response.id);

        self.loading.createExcelReport.fulfilled = true;
      } catch (e) {
        self.loading.createExcelReport.error = true;
        console.error(e);
      } finally {
        self.loading.createExcelReport.pending = false;
      }
    }),
    createReportLink: flow(function* () {
      try {
        self.loading.reportLink.fulfilled = false;
        self.loading.reportLink.error = false;
        self.loading.reportLink.pending = true;

        yield Reports.CreateReportLink({
          id: self.history_report_requests_id,
        });

        self.loading.reportLink.fulfilled = true;
      } catch (e) {
        self.loading.reportLink.error = true;
        console.error(e);
      } finally {
        self.loading.reportLink.pending = false;
      }
    }),
    patchSavedField: flow(function* () {
      try {
        //TODO: нет контроля состояний запроса
        yield Reports.PatchSavedField({
          id: self.history_report_requests_id,
        });
      } catch (e) {
        console.error(e);
      }
    }),
  }));
